<script lang="ts">
	import { onDestroy, onMount } from 'svelte';

	export let items: any[] = [];
	export let xl = 350;
	export let lg = 290;
	export let md: number | undefined;
	export let sm = 270;
	export let minimumItems = 48;

	const DESKTOP_MIN = 1124;
	const DESKTOP_WIDE = 1280;
	const MOBILE_MAX = 639;
	const DESKTOP_GAP = 16;
	const MOBILE_GAP = 8;
	const ESTIMATED_ROW_HEIGHT = 96;
	const OVERSCAN_ROWS = 3;

	let host: HTMLDivElement;
	let scrollParent: HTMLElement | Window;
	let hostResizeObserver: ResizeObserver | undefined;
	let animationFrame: number | undefined;
	let viewportWidth = DESKTOP_WIDE;
	let columns = 1;
	let startRow = 0;
	let endRow = 0;
	let rowHeights = new Map<number, number>();
	let layoutSignature = '';

	$: gap = viewportWidth <= MOBILE_MAX ? MOBILE_GAP : DESKTOP_GAP;
	$: rows = Array.from({ length: Math.ceil(items.length / columns) }, (_, rowIndex) => ({
		index: rowIndex,
		items: items.slice(rowIndex * columns, (rowIndex + 1) * columns),
	}));
	$: shouldVirtualize = items.length > minimumItems;
	$: visibleRows = shouldVirtualize ? rows.slice(startRow, endRow) : rows;
	$: topSpacerHeight = shouldVirtualize ? getRowsHeight(0, startRow) : 0;
	$: bottomSpacerHeight = shouldVirtualize ? getRowsHeight(endRow, rows.length) : 0;
	$: {
		const nextSignature = `${columns}:${items.map((item) => item?.id ?? '').join('|')}`;
		if (nextSignature !== layoutSignature) {
			layoutSignature = nextSignature;
			rowHeights = new Map();
			scheduleRangeUpdate();
		}
	}

	function getRowsHeight(from: number, to: number): number {
		let height = 0;
		for (let rowIndex = from; rowIndex < to; rowIndex += 1) {
			height += rowHeights.get(rowIndex) ?? ESTIMATED_ROW_HEIGHT;
			if (rowIndex < to - 1) height += gap;
		}
		return height;
	}

	function findRowAtOffset(offset: number): number {
		let currentOffset = 0;
		for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
			const rowHeight = rowHeights.get(rowIndex) ?? ESTIMATED_ROW_HEIGHT;
			if (currentOffset + rowHeight >= offset) return rowIndex;
			currentOffset += rowHeight + gap;
		}
		return Math.max(0, rows.length - 1);
	}

	function getScrollParent(element: HTMLElement): HTMLElement | Window {
		let parent = element.parentElement;
		while (parent) {
			const overflowY = getComputedStyle(parent).overflowY;
			if (overflowY === 'auto' || overflowY === 'scroll') return parent;
			parent = parent.parentElement;
		}
		return window;
	}

	function updateColumns(): void {
		if (!host) return;
		const width = host.clientWidth;
		const screenWidth = viewportWidth;
		const minWidth =
			screenWidth <= MOBILE_MAX
				? sm || md || lg || xl
				: screenWidth < DESKTOP_MIN
					? md || lg || sm || xl
					: screenWidth < DESKTOP_WIDE
						? lg || md || sm || xl
						: xl || lg || md || sm;
		columns = Math.max(1, Math.floor((width + gap) / (minWidth + gap)));
	}

	function updateVisibleRange(): void {
		animationFrame = undefined;
		if (!host || !shouldVirtualize || rows.length === 0) {
			startRow = 0;
			endRow = rows.length;
			return;
		}

		const hostRect = host.getBoundingClientRect();
		const viewportTop =
			scrollParent === window
				? Math.max(0, -hostRect.top)
				: Math.max(0, (scrollParent as HTMLElement).getBoundingClientRect().top - hostRect.top);
		const viewportBottom =
			scrollParent === window
				? window.innerHeight - hostRect.top
				: (scrollParent as HTMLElement).getBoundingClientRect().bottom - hostRect.top;

		const firstVisibleRow = findRowAtOffset(viewportTop);
		const lastVisibleRow = findRowAtOffset(Math.max(viewportTop, viewportBottom));
		startRow = Math.max(0, firstVisibleRow - OVERSCAN_ROWS);
		endRow = Math.min(rows.length, lastVisibleRow + OVERSCAN_ROWS + 1);
	}

	function scheduleRangeUpdate(): void {
		if (animationFrame !== undefined || typeof window === 'undefined') return;
		animationFrame = requestAnimationFrame(updateVisibleRange);
	}

	function handleWindowResize(): void {
		updateColumns();
		scheduleRangeUpdate();
	}

	function measureRow(node: HTMLElement, rowIndex: number) {
		const observer = new ResizeObserver(([entry]) => {
			const nextHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
			if (nextHeight > 0 && rowHeights.get(rowIndex) !== nextHeight) {
				rowHeights.set(rowIndex, nextHeight);
				rowHeights = new Map(rowHeights);
				scheduleRangeUpdate();
			}
		});
		observer.observe(node);

		return {
			destroy: () => observer.disconnect(),
		};
	}

	onMount(() => {
		viewportWidth = window.innerWidth;
		scrollParent = getScrollParent(host);
		updateColumns();
		updateVisibleRange();

		hostResizeObserver = new ResizeObserver(() => {
			updateColumns();
			scheduleRangeUpdate();
		});
		hostResizeObserver.observe(host);
		scrollParent.addEventListener('scroll', scheduleRangeUpdate, { passive: true });
		window.addEventListener('resize', handleWindowResize, { passive: true });
	});

	onDestroy(() => {
		hostResizeObserver?.disconnect();
		scrollParent?.removeEventListener('scroll', scheduleRangeUpdate);
		window.removeEventListener('resize', handleWindowResize);
		if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
	});
</script>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="virtual-card-grid" bind:this={host} style:--virtual-grid-gap="{gap}px">
	{#if topSpacerHeight > 0}
		<div class="virtual-card-grid-spacer" style:height="{topSpacerHeight}px"></div>
	{/if}

	{#each visibleRows as row (row.index)}
		<div
			class="virtual-card-grid-row"
			style:grid-template-columns="repeat({columns}, minmax(0, 1fr))"
			use:measureRow={row.index}
		>
			{#each row.items as item (item.id)}
				<div><slot {item} /></div>
			{/each}
		</div>
	{/each}

	{#if bottomSpacerHeight > 0}
		<div class="virtual-card-grid-spacer" style:height="{bottomSpacerHeight}px"></div>
	{/if}
</div>

<style>
	.virtual-card-grid {
		display: flex;
		flex-direction: column;
		gap: var(--virtual-grid-gap);
		padding-bottom: var(--virtual-grid-gap);
	}

	.virtual-card-grid-row {
		display: grid;
		gap: var(--virtual-grid-gap);
		align-items: stretch;
	}

	.virtual-card-grid-row > div {
		min-width: 0;
	}

	.virtual-card-grid-spacer {
		flex: 0 0 auto;
	}
</style>
